import { useEffect, useState } from "react";

import { DateTime } from "luxon";

import { CheckIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";

import useVehiclesMutations from "../../data/useVehiclesMutations";
import useApiHelper from "../../hooks/useApiHelper";
import Modal from "../../layouts/Modal";
import { ILead, ISingleLeadRespose } from "../../models/Lead";
import useNotification from "../notifications/useNotifications";

interface MarkCollectedModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  lead: ILead | null;
  onCollected?: (collectedOn: string) => void;
}

// Asks which day the vehicle was collected (today by default, never in the future) and saves it.
// The API moves the lead to Collected and records who did it when collectedOn goes from empty to set.
export default function MarkCollectedModal({ open, setOpen, lead, onCollected }: MarkCollectedModalProps) {
  const { get } = useApiHelper();
  const { update: updateVehicle } = useVehiclesMutations();
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const today = DateTime.now().toISODate()!;
  const [collectedOn, setCollectedOn] = useState(today);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setCollectedOn(DateTime.now().toISODate()!);
  }, [open]);

  const vrm = lead?.vehicle?.vrm ?? "Vehicle";
  const isValid = !!collectedOn && collectedOn <= today;

  const handleConfirm = async () => {
    if (!lead || !isValid) return;
    setSaving(true);

    try {
      // Table rows can carry a trimmed vehicle, and the vehicle PUT overwrites the address,
      // so always start from the full lead.
      const full = await get<ISingleLeadRespose>(`/lead/${lead.id}`);
      const vehicle = full.data.vehicle;

      if (!vehicle) throw new Error("This lead has no vehicle");

      await updateVehicle.mutateAsync({ ...vehicle, collectedOn });

      const label = collectedOn === today ? "today" : DateTime.fromISO(collectedOn).toFormat("d LLL");
      addNotification({ variant: "success", primaryText: `${vrm} marked collected ${label}` });

      queryClient.invalidateQueries(["pagedTable"]);
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["lead", lead.id]);

      onCollected?.(collectedOn);
      setOpen(false);
    } catch (e: any) {
      addNotification({ variant: "error", primaryText: e?.message ?? "Unable to mark as collected" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-md">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}
      >
        <div>
          <h3 className="text-lg font-medium leading-6 text-gray-900">Mark {vrm} as collected</h3>
          {lead?.contact?.fullname && <p className="mt-1 text-sm text-gray-600">{lead.contact.fullname}</p>}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="collectedOnDate" className="text-sm font-medium text-gray-700">
            Collected on
          </label>
          <input
            id="collectedOnDate"
            type="date"
            required
            max={today}
            value={collectedOn}
            onChange={(e) => setCollectedOn(e.target.value)}
            className="w-full rounded-md border border-gray-300 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
          {!isValid && <p className="text-xs text-red-700">Pick today or an earlier date.</p>}
        </div>

        <p className="text-xs text-gray-600">This moves the lead to Collected. Payment is not sent.</p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || saving}
            className="inline-flex items-center gap-1.5 rounded-md border border-green-700 bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 disabled:opacity-60"
          >
            <CheckIcon className="h-4 w-4" aria-hidden="true" />
            {saving ? "Saving…" : "Confirm collected"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
